import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlan } from '../../components/plan/usePlan';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { platformLabel } from '../../lib/platforms';
import { useReducedMotion } from '../../lib/useReducedMotion';
import type {
  DraftVariantRead,
  GenerationRequestDetailRead,
  GenerationRequestRead,
  SlideRead,
} from '../../types/api';
import { approveRequest } from '../generation/generationApi';
import { CaptionEditor } from './CaptionEditor';
import { FlagDialog } from './FlagDialog';
import { AI_TEXT_NOTE, LayerEditor, type PendingSlideEdit } from './LayerEditor';
import { LineagePanel } from './LineagePanel';
import { RejectionCapPanel } from './RejectionCapPanel';
import { ReviewBudget } from './ReviewBudget';
import { ReviewSplit } from './ReviewSplit';
import { SendBackDialog } from './SendBackDialog';
import { ShapedByLine } from './ShapedByLine';
import { SlideStrip } from './SlideStrip';
import { ThemeSwitcher } from './ThemeSwitcher';
import { useCommunityPreview } from './useCommunityPreview';
import { useFlagVariant } from './useFlagVariant';
import { useRejectRequest } from './useRejectRequest';
import { withRequestFields } from './useReviseVariant';
import { useSendBack } from './useSendBack';
import { restoreSlideDraft } from './useSlideDraft';
import { pendingRenderKey, useSlideRenderPolling } from './useSlideRenderPolling';
import { markPostAfterApprove } from './postAfterApprove';
import { VisualFindingsPanel } from './VisualFindingsPanel';

/** Message for the domain errors approval can provoke. */
function approvalMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REQUEST_NOT_AWAITING_REVIEW':
        return 'This post is no longer waiting for your approval. Reloading it now.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That approval did not save. Try again.');
}

interface FinalApprovalProps {
  businessId: string;
  request: GenerationRequestDetailRead;
  /** Server response, or a locally patched request after a slide edit. */
  onUpdated: (next: GenerationRequestDetailRead) => void;
  /** The request moved on without us; reload it. */
  onStale: () => void;
}

/**
 * Review Step 2 — final approval of whatever the pipeline rendered (SRS §3 stage
 * 7): a carousel's deck, or the single square a generated single came back as.
 * The screen is the same either way; only the wording follows the post type.
 *
 * Three decisions, and only three: approve it, send it back, or open the
 * constrained layer editor. The visual findings shown alongside are
 * advisory — the deterministic guardrail already ran server-side, so nothing on
 * this screen disables a button because of what a finding says. Reporting the
 * deck to Autopostmate staff sits beside those three and is not a fourth decision:
 * it mutates nothing and leaves every other action exactly where it was.
 *
 * **Send it back** is one control for every complaint, and the dialog behind it
 * is where the three prices live. A wording complaint revises the chosen
 * caption and re-plans the deck from the new words on the backdrops this run
 * already drew. A complaint about the pictures is a **redesign**: the caption
 * stands untouched and one design call re-plans the slides on the note — the
 * case that used to cost a whole new run to answer. Only a wrong angle is a
 * real regeneration, and only that one spends an attempt off the cap of three.
 *
 * The caption the deck carries is on this screen too, and editable: the words
 * that go out with the post are the user's, and fixing a typo here is free and
 * deterministic. It is not the same text as the words drawn on the slides —
 * those are the layer editor's — and the screen says so rather than leaving it
 * to be discovered.
 *
 * Changing the look sits in the action bar beside Edit layers: it redraws the
 * same approved words in another theme, so like an edit it is a re-render and
 * never a model call or a quota charge.
 *
 * Layout (`ReviewSplit`): the picture on the left, and on the right a sticky
 * panel with the heading, the caption, the action bar, the findings and the
 * budget, so Approve stays beside the picture it approves. The layer editor and
 * the earlier attempts run full width underneath.
 *
 * Slides arrive with `rendered_path` null while they are being drawn, and go
 * back to null after every layer edit or look change, so the screen polls the
 * request detail until the pictures land rather than making the user reload.
 */
export function FinalApproval({ businessId, request, onUpdated, onStale }: FinalApprovalProps) {
  const { can } = usePlan();
  // The post editor (layer edits and the look) is a plan feature. Without it
  // the editor opens view only; approving and exporting stay open.
  const canEdit = can('edit_posts');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingApproval, setIsConfirmingApproval] = useState(false);
  // "Post to {platform}": approve, then the finished screen opens the publish
  // (or connect) path. Only for platforms we can post to.
  const [postAfter, setPostAfter] = useState(false);
  const canPostTo = request.platform === 'linkedin' || request.platform === 'facebook';
  const [isApproving, setIsApproving] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  // The open editor's unsaved work, if it has any. A layer edit is saved by
  // hand on purpose — a save is a render — so approving has to write whatever
  // is still only on screen before it closes the request.
  const [pendingEdit, setPendingEdit] = useState<PendingSlideEdit | null>(null);
  const rejection = useRejectRequest(businessId, request.id, request.attempt);
  // A flag is raised against the copy the deck was rendered from — the variant
  // the user picked at step 1 — so there is nothing to disambiguate here.
  const flag = useFlagVariant(businessId, request.id, request.selected_variant_id);
  // The design and zones behind a community run, for the editor's stage: the
  // gallery entry, else the business's own template in whatever status. Null
  // for every other run, and when neither has it, where the stage degrades to
  // the classic block.
  const communityPreview = useCommunityPreview(businessId, request.community_template_id);

  const slides = [...request.slides].sort((left, right) => left.slide_index - right.slide_index);
  // The variant this deck was rendered from — the caption that ships with it.
  const chosen =
    request.variants.find((variant) => variant.id === request.selected_variant_id) ?? null;
  // A generated single is one square, not a deck: the wording, the slide numbers
  // and the strip's navigation all follow from this rather than from post type.
  const isDeck = request.post_type === 'carousel';
  const index = Math.min(currentIndex, Math.max(slides.length - 1, 0));
  const currentSlide = slides[index] ?? null;
  // A slide whose words the image model drew has no layers to edit (the server
  // answers 409); a redesign is how it changes. A look change re-renders the
  // whole deck, so one such slide takes the look off the bar.
  const isAiText = currentSlide?.text_in_image === true;
  const hasAiText = slides.some((slide) => slide.text_in_image === true);
  const pendingKey = pendingRenderKey(slides);
  const isRendering = pendingKey !== null;
  // Which slide the unsaved edits belong to, for the confirmation. Null on a
  // one-picture run, where a number would be numbering a set of one.
  const pendingEditIndex =
    pendingEdit === null ? -1 : slides.findIndex((slide) => slide.id === pendingEdit.slideId);
  const pendingSlideNumber =
    slides.length > 1 && pendingEditIndex >= 0 ? pendingEditIndex + 1 : null;

  const { isSlow } = useSlideRenderPolling(businessId, request.id, pendingKey, onUpdated);

  // The layer editor opens full width under the two columns; bring it to the
  // user rather than leaving it below the fold of a sticky panel.
  const editorRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (!isEditing) return;
    editorRef.current?.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [isEditing, reducedMotion]);

  /** A saved slide replaces its own row; the rest of the request is untouched. */
  const applySlide = useCallback(
    (saved: SlideRead) => {
      onUpdated({
        ...request,
        slides: request.slides.map((slide) => (slide.id === saved.id ? saved : slide)),
      });
    },
    [onUpdated, request],
  );

  /** A rewritten caption replaces its own variant; nothing is re-rendered. */
  const applyVariant = useCallback(
    (saved: DraftVariantRead) => {
      onUpdated({
        ...request,
        variants: request.variants.map((variant) => (variant.id === saved.id ? saved : variant)),
      });
    },
    [onUpdated, request],
  );

  /**
   * A revision hands back the request itself as `revising`, a redesign as
   * `designing`. Either way it is this same row, so it goes through the path a
   * selection takes: the page swaps to the stage tracker and the stream
   * resubscribes on a fresh token, and the run lands back here with a redrawn
   * deck.
   */
  const applySentBack = useCallback(
    (next: GenerationRequestRead) => onUpdated(withRequestFields(request, next)),
    [onUpdated, request],
  );

  const sendBack = useSendBack(businessId, request, chosen, applySentBack, rejection);

  /** Stable, so the editor can report in and out without re-registering. */
  const handleUnsavedEdit = useCallback((pending: PendingSlideEdit | null) => {
    setPendingEdit(pending);
  }, []);

  async function handleApprove() {
    if (isApproving) return;

    setApprovalError(null);
    setIsApproving(true);

    // Save first, approve second. A guardrail refusal here stops the whole
    // thing: approving a deck whose slides do not match what is on screen
    // would be worse than making the user look at the error.
    if (pendingEdit !== null) {
      setIsFlushing(true);
      try {
        await pendingEdit.flush();
      } catch (caught) {
        setApprovalError(
          `${getErrorMessage(caught, 'Those slide edits did not save.')} Nothing was approved.`,
        );
        setIsFlushing(false);
        setIsApproving(false);
        setIsConfirmingApproval(false);
        return;
      }
      setIsFlushing(false);
    }

    // The flush above covers the open editor. A slide whose editor was closed
    // can still be holding recovered work in localStorage — invisible here, so
    // it cannot be saved on the user's behalf (its validation lives in the
    // editor). Approving over it would ship a deck that silently drops edits
    // the user believes they made; blocking with directions beats that.
    // View only cannot save or discard such a draft, so it does not block there.
    const strandedIndex = !canEdit
      ? -1
      : slides.findIndex(
          (slide) => slide.id !== pendingEdit?.slideId && restoreSlideDraft(slide).savedAt !== null,
        );
    if (strandedIndex >= 0) {
      const which = slides.length > 1 ? `Slide ${strandedIndex + 1} has` : 'This slide has';
      setApprovalError(
        `${which} unsaved changes from an earlier session. Open it and save or discard ` +
          'them first. Nothing was approved.',
      );
      setIsApproving(false);
      setIsConfirmingApproval(false);
      return;
    }

    try {
      const approved = await approveRequest(businessId, request.id);
      if (postAfter) markPostAfterApprove(request.id);
      onUpdated(approved);
    } catch (caught) {
      setApprovalError(approvalMessage(caught));
      setIsApproving(false);
      setIsConfirmingApproval(false);
      if (caught instanceof ApiError && caught.code === 'REQUEST_NOT_AWAITING_REVIEW') {
        onStale();
      }
    }
  }

  function startApproval(thenPost = false) {
    setApprovalError(null);
    sendBack.close();
    setPostAfter(thenPost);
    setIsConfirmingApproval(true);
  }

  if (slides.length === 0) {
    return (
      <p className="form__notice">
        This post is waiting for your approval but has nothing drawn yet. Reload, or create a new
        post.
      </p>
    );
  }

  const isBusy = isApproving || sendBack.isSubmitting;

  const picture = (
    <>
      <SlideStrip
        slides={slides}
        currentIndex={index}
        onSelect={(next) => {
          setCurrentIndex(next);
          // The editor edits one slide; moving on closes it rather than
          // quietly re-pointing it at a different slide's text.
          setIsEditing(false);
        }}
        isSlow={isSlow}
      />

      {isRendering ? (
        <p className="form__notice" role="status">
          {isSlow
            ? 'This is taking longer than expected. We are still working on it — it will appear here when it lands.'
            : 'Still rendering. It appears here as soon as it is drawn.'}
        </p>
      ) : null}
    </>
  );

  return (
    <>
      <ReviewSplit picture={picture}>
        <section className="final-approval">
          <header className="final-approval__head">
            <h2 className="final-approval__title">
              {isDeck ? 'Approve the carousel' : 'Approve the image'}
            </h2>
            <p className="final-approval__lede">
              {slides.length === 1
                ? 'Rendered from the caption you picked.'
                : `${slides.length} slides, rendered from the caption you picked.`}
            </p>
          </header>

          {chosen ? (
            <div className="final-approval__caption">
              <h3 className="final-approval__caption-title">Caption</h3>
              <CaptionEditor
                businessId={businessId}
                variant={chosen}
                platform={request.platform}
                isDisabled={isBusy}
                onSaved={applyVariant}
                onStale={onStale}
              />
              {/* One line: the caption ships with the post; the words drawn on
                  the picture are the layer editor's. */}
              <p className="final-approval__caption-note">
                Goes out with the post.
                {hasAiText
                  ? null
                  : ` The words on the ${isDeck ? 'slides' : 'image'} are edited in the layer editor.`}
              </p>
              <ShapedByLine request={request} />
            </div>
          ) : null}

          {approvalError ? (
            <p className="form__error" role="alert">
              {approvalError}
            </p>
          ) : null}

          {rejection.showCapPanel ? (
            <RejectionCapPanel
              step="visual"
              isBusy={isBusy}
              onAcceptBest={() => {
                rejection.dismissCap();
                startApproval();
              }}
              onEditLayers={
                isAiText
                  ? undefined
                  : () => {
                      rejection.dismissCap();
                      setIsEditing(true);
                    }
              }
            />
          ) : null}

          {isConfirmingApproval ? (
            <div className="approve-confirm">
              <p className="approve-confirm__message">
                {slides.length === 1
                  ? 'Approve this image and the caption it carries?'
                  : `Approve these ${slides.length} slides and the caption they carry?`}{' '}
                {postAfter
                  ? `This closes the post, then asks once more before it goes out on ${platformLabel(request.platform)}.`
                  : 'This closes the post. Approving still puts nothing out: you download it from here, or publish it as a separate, explicit step.'}
              </p>
              {pendingEdit === null ? null : (
                <p className="approve-confirm__message">
                  {pendingSlideNumber === null
                    ? 'This image has edits you have not saved yet.'
                    : `Slide ${pendingSlideNumber} has edits you have not saved yet.`}{' '}
                  Approving saves them and re-renders first.
                </p>
              )}
              <div className="approve-confirm__actions">
                <button
                  className="button button--primary"
                  type="button"
                  disabled={isApproving}
                  onClick={() => void handleApprove()}
                >
                  {isFlushing
                    ? 'Saving changes'
                    : isApproving
                      ? 'Approving'
                      : postAfter
                        ? 'Yes, approve and continue'
                        : 'Yes, approve'}
                </button>
                <button
                  className="button"
                  type="button"
                  disabled={isApproving}
                  onClick={() => setIsConfirmingApproval(false)}
                >
                  Not yet
                </button>
              </div>
            </div>
          ) : (
            <div className="final-approval__actions">
              <button
                className="button button--primary"
                type="button"
                disabled={isBusy}
                onClick={() => startApproval()}
              >
                Approve
              </button>

              {canPostTo ? (
                <button
                  className="button"
                  type="button"
                  disabled={isBusy}
                  onClick={() => startApproval(true)}
                >
                  Post to {platformLabel(request.platform)}
                </button>
              ) : null}

              {/* One control for every complaint. It stays on screen at the
                  rejection cap, because a revision and a redesign are not
                  attempts — what the cap closes is the regenerate path inside it. */}
              <button
                className="button button--danger"
                type="button"
                disabled={isBusy || sendBack.isOpen}
                onClick={sendBack.open}
              >
                Send it back
              </button>

              {isAiText ? null : (
                <button
                  className="button"
                  type="button"
                  disabled={isBusy || isEditing}
                  onClick={() => {
                    sendBack.close();
                    setIsEditing(true);
                  }}
                >
                  {canEdit ? 'Edit layers' : 'View layers'}
                </button>
              )}

              {/* Changing the look is a re-render, like a layer edit: no model
                  call, nothing off the quota. A post-editor write, so it is
                  off the bar on a plan without the editor. */}
              {canEdit && !hasAiText ? (
                <ThemeSwitcher
                  businessId={businessId}
                  request={request}
                  isRendering={isRendering}
                  variant="button"
                  onChanged={(next) => {
                    // Every slide comes back unrendered, which is the poll's own
                    // cue — the pictures reappear one by one as after a layer edit.
                    setIsEditing(false);
                    onUpdated(next);
                  }}
                  onStale={onStale}
                />
              ) : null}

              {/* Advisory, like the findings below: reporting changes nothing
                  about this request, so it is never disabled by `isBusy` and
                  never disables anything else. A quiet text control. */}
              {flag.isFlagged ? (
                <p className="flag-note" role="status">
                  Flagged for review
                </p>
              ) : request.selected_variant_id ? (
                <button className="flag-link" type="button" onClick={flag.open}>
                  Flag this content
                </button>
              ) : null}
            </div>
          )}

          {sendBack.isOpen ? (
            <SendBackDialog
              step="visual"
              subject={isDeck ? 'carousel' : 'image'}
              regenerationsUsed={sendBack.regenerationsUsed}
              revisionsUsed={sendBack.revisionsUsed}
              isRegenerationCapped={sendBack.isRegenerationCapped}
              isRevisionCapped={sendBack.isRevisionCapped}
              isSubmitting={sendBack.isSubmitting}
              error={sendBack.error}
              onReasonChange={sendBack.clearError}
              onSubmit={sendBack.submit}
              onCancel={sendBack.close}
            />
          ) : null}

          {isAiText ? <p className="final-approval__caption-note">{AI_TEXT_NOTE}</p> : null}

          {currentSlide ? (
            <VisualFindingsPanel
              findings={currentSlide.visual_findings}
              // One picture needs no number to tell it from the others.
              slideNumber={slides.length > 1 ? currentSlide.slide_index + 1 : null}
            />
          ) : null}

          <ReviewBudget attempt={request.attempt} revisions={request.revisions} />
        </section>
      </ReviewSplit>

      <LineagePanel lineage={request.lineage} attempt={request.attempt} />

      {isEditing && currentSlide && !isAiText ? (
        <div className="final-approval__editor" ref={editorRef}>
          <LayerEditor
            key={currentSlide.id}
            businessId={businessId}
            requestId={request.id}
            slide={currentSlide}
            isOnlySlide={slides.length === 1}
            backdrops={request.backdrops}
            community={communityPreview}
            onSaved={applySlide}
            onClose={() => setIsEditing(false)}
            onStale={onStale}
            onUnsavedChange={handleUnsavedEdit}
            readOnly={!canEdit}
          />
        </div>
      ) : null}

      {flag.isOpen ? (
        <FlagDialog
          subject={isDeck ? 'this carousel' : 'this image'}
          isSubmitting={flag.isSubmitting}
          error={flag.error}
          onSubmit={flag.submit}
          onDismiss={flag.close}
        />
      ) : null}
    </>
  );
}
